// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { css } from "styled-components";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

import { StyledButton } from "@insite/mobius/Clickable";
import { screen } from "@rugby/XDL/Helpers";
import { extendedMaxWidths } from "@rugby/XDL/PreStyle/breakpoints";
import { footerColor } from "@rugby/XDL/PreStyle/colors";

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    productsGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
}

const headingStyles = `
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: normal;
    text-transform: uppercase;
    transform: translateY(-5px);
`;

const linkStyles = `
    color: ${footerColor.contrast};
    font-size: 14px;
    text-transform: capitalize;

    &:hover {
        color: ${footerColor.contrast};
        text-decoration: underline;

        span {
            color: ${footerColor.contrast};
        }
    }

    span {
        color: ${footerColor.contrast};
    }
`;

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            background-color: transparent;
            & > div {
                display: flex !important;

                ${screen("lte", "sm")`
                    margin: 0;
                `}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 10px;
                        `,
                        css`
                            padding: 0 10px;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[2]}px;
                            margin: 0 auto;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[3]}px;
                            margin: 0 auto;
                        `,
                        css`
                            max-width: ${extendedMaxWidths[4]}px;
                            margin: 0 auto;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    productsGridItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
            color: ${footerColor.contrast};
            padding: 0 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            &&& {
                                margin-bottom: 20px;
                            }
                        `,
                        css`
                            &&& {
                                margin-bottom: 20px;
                            }
                        `,
                        css`
                            margin-bottom: 40px;
                            border-right: 1px solid ${footerColor.contrast};
                        `,
                        css`
                            border-right: 1px solid ${footerColor.contrast};
                        `,
                        css`
                            border-right: 1px solid ${footerColor.contrast};
                        `,
                    ],
                    "min",
                )}

            h3 {
                ${headingStyles}
                ${screen("lte", "sm")`
                    transform: none;
                `}
            }

            ${StyledButton} {
                margin-bottom: 10px;
                ${linkStyles}
            }

            div > div {
                display: flex;
                flex-wrap: wrap;
                flex-direction: row;

                div {
                    flex: auto;
                    width: 50%;

                    &.titleWrapper {
                        width: 100%;
                        margin-bottom: 15px;
                    }
                }
            }
        `,
    },
    contentGridItem: {
        width: [6, 6, 6, 2, 2],
        css: css`
            color: ${footerColor.contrast};
            padding: 0 20px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            &&& {
                                margin-bottom: 20px;
                            }
                        `,
                        css`
                            &&& {
                                margin-bottom: 20px;
                            }
                        `,
                        css`
                            margin-bottom: 40px;
                            &:nth-child(odd) {
                                border-right: 1px solid ${footerColor.contrast};
                            }
                        `,
                        css`
                            margin-bottom: 40px;
                            border-right: 1px solid ${footerColor.contrast};
                        `,
                        css`
                            border-right: 1px solid ${footerColor.contrast};
                        `,
                    ],
                    "min",
                )}

            h3 {
                ${headingStyles}
            }
            ${StyledButton} {
                margin-bottom: 10px;
            }
            a [class*="StyledTypography"] {
                ${linkStyles}
            }

            .linkList {
                .titleWrapper {
                    margin-bottom: 15px;
                }
                .link {
                    a:hover > span {
                        color: ${footerColor.contrast};
                    }
                }
            }
        `,
    },
    subscribeGridItem: {
        width: [6, 6, 6, 3, 3],
        css: css`
            color: ${footerColor.contrast};
            padding: 10px 20px 0;

            h3 {
                ${headingStyles}
                transform: translateY(-5px);
            }

            .socialLinkList {
                margin-bottom: 50px;

                .linkWrapper {
                    flex-basis: auto;
                    flex-shrink: 1;
                    padding: 0 10px 0 0;
                }
            }
        `,
    },
};
